import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { AddressBox, InfoBox, InfoPanel, Loading, TextBox, UnitMap, UnitPanel } from '../../components';
import { useTripStoreUpdater } from '../../hooks';
import { StoreState } from '../../types/store.type';
import { ReservationUtils, TranslationUtils } from '../../utils';
import ReactPlayer from 'react-player';
import _ from 'lodash';
import './GettingThere.scss';
import { QuestionPanel } from '../pal/reservation/components';

export const GettingThere: React.FC = () => {
    useTripStoreUpdater(true);

    const trip = useSelector((store: StoreState) => store.trip);

    if (trip.isLoading) {
        return <Loading />;
    }

    const { access, unit, reservation } = trip;

    if (!access || !unit) {
        return null;
    }

    const maskCode = '****';

    const isElockNullOrEmpty = (eLockCodes: any[] | null) => {
        return !Array.isArray(eLockCodes) || _.isEmpty(eLockCodes);
    };

    const shouldShowLockbox = (lockBox: string | null): boolean => {
        return !_.isEmpty(lockBox);
    };

    const {
        driving_directions,
        access_notes,
        lockbox,
        alarm,
        is_masked,
        masked_note,
        gate,
        lockbox_masked_note,
        elocks_instructions_videos,
    } = access;

    let { elocks } = access;
    elocks = _.filter(elocks, (elock) => !_.isEmpty(elock.access_code));

    const { vehicle_info } = unit;

    const parkingNotes = vehicle_info.parking_instructions;
    const showLockbox: boolean = shouldShowLockbox(lockbox);
    const isPalTrip = ReservationUtils.isPalManaged(reservation);

    const getFormatUrl = (info: string | null): string | JSX.Element => {
        const isUrl: boolean = typeof info == 'string' && info.search('https://') == 0;
        return isUrl ? (
            <a href={info as string} target="_blank" className="address-link">
                {TranslationUtils.get('click_here_to_get_code')}
            </a>
        ) : (
            info || ''
        );
    };

    const pm = unit.pms_data?.property_management;

    return (
        <div>
            <h1 tabIndex={0} className="heading-large page-title">
                {TranslationUtils.get('getting_there')}
            </h1>

            <UnitPanel />

            <Row>
                {isPalTrip ? (
                    <>
                        <Col className="mb-4" sm={6}>
                            <UnitMap lat={unit.latitude} lng={unit.longitude} />
                        </Col>
                        <Col className="mb-4" sm={6}>
                            <InfoPanel title={TranslationUtils.get('location')}>
                                <AddressBox compact={false} />
                            </InfoPanel>
                        </Col>
                    </>
                ) : (
                    <Col className="mb-4" sm={6}>
                        <InfoPanel title={TranslationUtils.get('location_and_parking')}>
                            <AddressBox />
                            <InfoBox info={vehicle_info.parking_total_cars || 0} subtitle={TranslationUtils.get('parking_spaces')} />
                        </InfoPanel>
                    </Col>
                )}

                {!!(alarm || showLockbox) ? (
                    <Col className="mb-4" sm={6}>
                        <InfoPanel
                            title={TranslationUtils.get('entering_home')}
                            alert={
                                is_masked ? lockbox_masked_note || masked_note || TranslationUtils.get('codes_will_be_provided') : undefined
                            }
                        >
                            {alarm && (
                                <InfoBox info={is_masked ? maskCode : getFormatUrl(alarm)} subtitle={TranslationUtils.get('alarm_code')} />
                            )}
                            {showLockbox && lockbox && (
                                <InfoBox
                                    info={is_masked ? maskCode : getFormatUrl(lockbox)}
                                    subtitle={TranslationUtils.get('lockbox_code')}
                                />
                            )}
                        </InfoPanel>
                    </Col>
                ) : null}

                {elocks && !isElockNullOrEmpty(elocks) && (
                    <Col className="mb-4" sm={6}>
                        <InfoPanel
                            title={TranslationUtils.get('e_lock_codes')}
                            alert={is_masked ? masked_note || TranslationUtils.get('codes_will_be_provided') : undefined}
                        >
                            {elocks.map((eLock) => {
                                return (
                                    <InfoBox
                                        info={is_masked ? maskCode : getFormatUrl(eLock.access_code)}
                                        subtitle={is_masked || !eLock.property_access_description ? '' : eLock.property_access_description}
                                    />
                                );
                            })}
                        </InfoPanel>
                    </Col>
                )}
                {!_.isEmpty(elocks_instructions_videos) &&
                    elocks_instructions_videos.map((value, index) => (
                        <Col className="video-columns mb-4" sm={6}>
                            <div key={index} className={'player-wrapper'}>
                                <ReactPlayer className="react-player" url={value.video_url} width={'100%'} height={'100%'} />
                            </div>
                        </Col>
                    ))}
            </Row>

            {!_.isEmpty(gate) && typeof gate === 'string' && !is_masked && (
                <TextBox
                    title={TranslationUtils.get('gate_access')}
                    text={gate}
                    maxLength={400}
                    classTitle={'body-bold page-subtitle-notes'}
                    classNote={'body-small page-text-notes'}
                />
            )}

            <div tabIndex={0}>
                {driving_directions && (
                    <TextBox
                        title={TranslationUtils.get('directions_to_know')}
                        classTitle={'body-bold page-subtitle-notes'}
                        classNote={'body-small page-text-notes'}
                        inline={true}
                        text={is_masked ? masked_note || '' : driving_directions}
                        maxLength={400}
                    />
                )}

                <div>
                    {access_notes && !is_masked && (
                        <TextBox
                            title={TranslationUtils.get('home_access_details')}
                            classTitle={'body-bold page-subtitle-notes'}
                            classNote={'body-small page-text-notes'}
                            inline={true}
                            text={is_masked ? masked_note || '' : access_notes}
                            maxLength={400}
                        />
                    )}

                    {parkingNotes && parkingNotes.trim() != '' && (
                        <TextBox
                            title={TranslationUtils.get('parking_notes')}
                            classTitle={'body-bold page-subtitle-notes'}
                            classNote={'body-small page-text-notes'}
                            inline={true}
                            text={parkingNotes}
                            maxLength={400}
                        />
                    )}
                    {ReservationUtils.isPalManaged(trip.reservation) && pm?.name && (pm?.email || pm?.phone) && (
                        <QuestionPanel name={pm.name} email={pm.email ?? undefined} phoneNumber={pm.phone ?? undefined} />
                    )}
                </div>
            </div>
        </div>
    );
};
